import { useLocation, useNavigate } from 'react-router-dom';
import { LinkProps } from '../..';
import { getInternalLink } from '../../utils';

export interface MemorizedLocationState {
  prevLocation?: ReturnType<typeof useLocation>;
}

export const useMemorizedLink = () => {
  const location = useLocation<MemorizedLocationState>();
  const navigate = useNavigate();

  /**
   * Memorizes the current location and returns the link to navigate to with the memorized location in the state
   * @param to - Link to redirect to
   * @returns Link to navigate to with the memorized location in the state
   */
  const memorizeCurrentLocation = (
    to: LinkProps['to'],
    state?: any,
  ) => {

    const link = getInternalLink(to);

    const adjustedState = typeof state === 'object' ? state : {};

    return {
      to: link,
      state: {
        ...adjustedState,
        prevLocation: location
      }
    };
  };

  /**
   * Returns the memorized location if it exists, otherwise the default link
   * @param defaultLink Link to navigate to if there is no memorized location
   * @returns Memorized location if it exists, otherwise the default link
   */
  const getMemorizedLink = (
    defaultLink: LinkProps['to'],
    state?: any
  ): {
    to: LinkProps['to'];
    state?: any;
  } => {
    const memorizedLocation = location.state?.prevLocation;
    if (!memorizedLocation) return { to: getInternalLink(defaultLink), state };

    const { state: memorizedState, ...to } = memorizedLocation;

    const adjustedState = typeof state === 'object' ? state : {};
    const adjustedMemorizedState = typeof memorizedState === 'object' ? memorizedState : {};

    return { to, state: { ...adjustedState, ...adjustedMemorizedState } };
  };

  /**
   * Persists the memorized location if present and returns the link to navigate to with the memorized location in the state
   * @param to - Link to navigate to
   * @returns Link to navigate to with the memorized location in the state if present or the default link without the memorized location
   */
  const persistMemorizedLocation = (to: LinkProps['to'], state?: any): {
    to: LinkProps['to'];
    state?: any;
  } => {
    const memorizedLocation = location.state?.prevLocation;
    if (!memorizedLocation) return { to: getInternalLink(to) };

    const link = getInternalLink(to);

    const adjustedState = typeof state === 'object' ? state : {};

    return {
      to: link,
      state: {
        ...adjustedState,
        prevLocation: memorizedLocation
      }
    };
  };

  const navigateToMemorizedLink = (to: LinkProps['to']) => {
    const { to: linkToNavigate, state: memorizedState } = getMemorizedLink(to);
    if (memorizedState) {
      navigate(linkToNavigate, { state: memorizedState });
    } else {
      navigate(linkToNavigate);
    }
  };

  return {
    memorizeCurrentLocation,
    getMemorizedLink,
    persistMemorizedLocation,
    navigateToMemorizedLink
  };
};